import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import logoADUTEM from "../../assets/img/logoadutem.png";

// ───────────────────────────────────────────────
// Componente principal: MisReservas
// ───────────────────────────────────────────────
const MisReservas = () => {
  const navigate = useNavigate();

  const [reservas, setReservas] = useState([]);
  const [usuario, setUsuario] = useState(null);     // ← Nuevo estado
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filtroEstado, setFiltroEstado] = useState("TODOS");
  const [filtroEspacio, setFiltroEspacio] = useState("TODOS");

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [perfilRes, reservasRes] = await Promise.all([
          axios.get("/perfil"),           // ← Extrae nombre del socio
          axios.get("/mis-reservas"),
        ]);

        // Guardar datos del perfil
        if (perfilRes.data?.ok) {
          setUsuario(perfilRes.data.perfil || perfilRes.data.usuario || null);
        }

        // Guardar reservas
        if (reservasRes.data?.ok) {
          setReservas(reservasRes.data.reservas || []);
        } else {
          setError(reservasRes.data?.message || "Respuesta inválida del servidor");
        }
      } catch (err) {
        console.error("Error al cargar mis reservas:", err);
        const mensaje =
          err.response?.data?.message ||
          err.response?.data?.msg ||
          err.message ||
          "No se pudieron cargar tus reservas";
        setError(mensaje);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filtros
  const espaciosUnicos = ["TODOS", ...new Set(reservas.map(r => r.espacio?.nombre || "Sin espacio"))];
  const estadosUnicos = ["TODOS", "PENDIENTE", "APROBADA", "RECHAZADA"];

  const reservasFiltradas = reservas.filter(r => {
    const coincideEstado = filtroEstado === "TODOS" || r.estado === filtroEstado;
    const coincideEspacio = filtroEspacio === "TODOS" || r.espacio?.nombre === filtroEspacio;
    return coincideEstado && coincideEspacio;
  });

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={() => navigate(0)} />;

  // Extraemos el nombre exactamente como en DashboardSocio
  const socioNombre = usuario?.nombres || "Socio";
  const socioApellido = usuario?.apellidos || "";
  const nombreCompleto = `${socioNombre} ${socioApellido}`.trim();
  const primeraLetra = socioNombre.charAt(0).toUpperCase() || "S";

  return (
    <>
      {/* Navbar Mejorado */}
      <Navbar socioNombre={nombreCompleto} primeraLetra={primeraLetra} />

      <main className="container py-5">
        <header className="mb-5">
          <h1 className="fw-bold" style={{ color: "#2c3e50" }}>
            <i className="bi bi-calendar-check me-3 text-success"></i>
            Mis Reservas
          </h1>
          <p className="text-muted">Historial de tus reservas realizadas</p>
        </header>

        {/* Filtros */}
        <div className="row g-3 mb-5">
          <div className="col-md-4 col-sm-6">
            <label htmlFor="filtroEstado" className="form-label fw-medium text-muted">
              Estado
            </label>
            <select
              id="filtroEstado"
              className="form-select"
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
            >
              {estadosUnicos.map(estado => (
                <option key={estado} value={estado}>
                  {estado === "TODOS" ? "Todos los estados" : estado}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4 col-sm-6">
            <label htmlFor="filtroEspacio" className="form-label fw-medium text-muted">
              Espacio
            </label>
            <select
              id="filtroEspacio"
              className="form-select"
              value={filtroEspacio}
              onChange={(e) => setFiltroEspacio(e.target.value)}
            >
              {espaciosUnicos.map(espacio => (
                <option key={espacio} value={espacio}>
                  {espacio === "TODOS" ? "Todos los espacios" : espacio}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4 col-sm-6 d-flex align-items-end">
            <small className="text-muted">
              {reservasFiltradas.length} reserva{reservasFiltradas.length !== 1 ? "s" : ""} encontrada{reservasFiltradas.length !== 1 ? "s" : ""}
            </small>
          </div>
        </div>

        {reservasFiltradas.length === 0 ? (
          <div className="alert alert-info text-center py-5 shadow-sm">
            <i className="bi bi-calendar-x fs-1 me-2"></i>
            {reservas.length === 0
              ? "Aún no tienes reservas registradas"
              : "Ninguna reserva coincide con los filtros seleccionados"}
          </div>
        ) : (
          <div className="row g-4">
            {reservasFiltradas.map(reserva => (
              <div className="col-lg-4 col-md-6" key={reserva.id}>
                <div className="card h-100 shadow-sm border-0 overflow-hidden position-relative">
                  {/* Badge de estado */}
                  <span
                    className={`badge position-absolute top-0 end-0 m-3 fs-6 px-3 py-2 text-uppercase shadow-sm ${
                      reserva.estado === "APROBADA" ? "bg-success" :
                      reserva.estado === "PENDIENTE" ? "bg-warning text-dark" :
                      reserva.estado === "RECHAZADA" ? "bg-danger" :
                      "bg-secondary"
                    }`}
                  >
                    {reserva.estado}
                  </span>

                  {/* Imagen del espacio */}
                  {reserva.espacio?.imagen ? (
                    <img
                      src={reserva.espacio.imagen}
                      alt={reserva.espacio.nombre}
                      className="card-img-top"
                      style={{ height: "160px", objectFit: "cover" }}
                      onError={e => { e.target.src = "https://via.placeholder.com/400x160?text=Espacio"; }}
                    />
                  ) : (
                    <div className="bg-light d-flex align-items-center justify-content-center" style={{ height: "160px" }}>
                      <i className="bi bi-building fs-1 text-muted"></i>
                    </div>
                  )}

                  <div className="card-body">
                    <h5 className="card-title fw-bold mb-2">
                      {reserva.espacio?.nombre || "Espacio no especificado"}
                    </h5>

                    <div className="text-muted small mb-3">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <i className="bi bi-calendar-event"></i>
                        {reserva.fecha}
                      </div>
                      {reserva.hora_inicio && (
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <i className="bi bi-clock"></i>
                          {reserva.hora_inicio.slice(0,5)} – {reserva.hora_fin.slice(0,5)}
                        </div>
                      )}
                      {reserva.tipo_piscina && (
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <i className="bi bi-water"></i>
                          {reserva.tipo_piscina}
                        </div>
                      )}
                    </div>

                    {reserva.observacion && (
                      <p className="small text-muted border-top pt-2 mt-2">
                        <i className="bi bi-chat-left-text me-1"></i>
                        {reserva.observacion}
                      </p>
                    )}
                  </div>

                  <div className="card-footer bg-white border-0 text-muted small py-2">
                    <i className="bi bi-calendar-plus me-1"></i>
                    Creada el {new Date(reserva.created_at).toLocaleDateString("es-EC")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </>
  );
};

// ───────────────────────────────────────────────
// NAVBAR MEJORADO (sin cambios)
// ───────────────────────────────────────────────
const Navbar = ({ socioNombre, primeraLetra }) => {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login", { replace: true });
  };

  return (
    <nav 
      className="navbar navbar-expand-lg navbar-dark shadow-sm"
      style={{
  background: "#FFD123",
  padding: "18px 0",
  position: "sticky",
  top: 0,
  zIndex: 1000,
}}
    >
      <div className="container-fluid px-4">
        
        <Link className="navbar-brand d-flex align-items-center" to="/DashboardSocio">
          <img 
            src={logoADUTEM} 
            alt="ADUTEM" 
            style={{ height: "68px" }} 
          />
        </Link>

        <button
          className="navbar-toggler border-0"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav mx-auto gap-1">
            {[
              { to: "/DashboardSocio", label: "Inicio", end: true },
              { to: "/vercuotas", label: "Mis Cuotas" },
              { to: "/reservasSocios", label: "Reservas" },
              { to: "/MisReservas", label: "Mis Reservas" },
            ].map(({ to, label, end = false }) => (
              <li className="nav-item" key={to}>
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) => 
                    `nav-link px-4 py-2 rounded-pill fw-medium transition-all ${isActive 
                      ? "active bg-white text-dark shadow-sm" 
                      : "text-white hover-bg-white hover-text-dark"}`
                  }
                  style={{ fontSize: "1.02rem" }}
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="dropdown">
            <button
              className="btn btn-link text-decoration-none p-0 border-0 d-flex align-items-center gap-2"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <div
                className="rounded-circle d-flex align-items-center justify-content-center shadow"
                style={{
                  width: "42px",
                  height: "42px",
                  backgroundColor: "#f1c40f",
                  color: "#2c3e50",
                  fontWeight: "700",
                  fontSize: "1.25rem",
                  border: "2px solid rgba(255,255,255,0.6)"
                }}
              >
                {primeraLetra}
              </div>
              <div className="text-white text-start d-none d-md-block">
                <div className="fw-medium" style={{ fontSize: "1.02rem" }}>{socioNombre}</div>
                <small className="opacity-75" style={{ fontSize: "0.82rem" }}>Socio</small>
              </div>
              <i className="bi bi-chevron-down text-white ms-1" style={{ fontSize: "1rem" }}></i>
            </button>

            <ul className="dropdown-menu dropdown-menu-start shadow-lg border-0 py-2">
              <li>
                <Link className="dropdown-item d-flex align-items-center py-2" to="/perfil">
                  <i className="bi bi-person-circle me-3 fs-5"></i>
                  Mi Perfil
                </Link>
              </li>
              <li><hr className="dropdown-divider my-1" /></li>
              <li>
                <button 
                  className="dropdown-item d-flex align-items-center py-2 text-danger" 
                  onClick={handleLogout}
                >
                  <i className="bi bi-box-arrow-right me-3 fs-5"></i>
                  Cerrar sesión
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Loading y Error States
const LoadingState = () => (
  <div className="min-vh-100 d-flex align-items-center justify-content-center">
    <div className="text-center">
      <div className="spinner-border text-success" style={{ width: "3.5rem", height: "3.5rem" }} role="status">
        <span className="visually-hidden">Cargando...</span>
      </div>
      <p className="mt-4 text-muted fs-5">Cargando tus reservas...</p>
    </div>
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="min-vh-100 d-flex align-items-center justify-content-center p-4">
    <div className="alert alert-danger text-center shadow-lg p-5" style={{ maxWidth: "500px" }}>
      <i className="bi bi-exclamation-triangle-fill me-3 fs-3"></i>
      <h4 className="alert-heading mb-3">Algo salió mal</h4>
      <p className="mb-4">{message || "No pudimos cargar tus reservas"}</p>
      <button className="btn btn-outline-danger px-5" onClick={onRetry}>
        Reintentar
      </button>
    </div>
  </div>
);

export default MisReservas;